import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { CommentResponse, hasAnsweredResponse, LikesResponse, QuestionResponse, simpleResponse } from '../models/question.model';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class QuestionService {
  private url = environment.api; // URL da API
  private token = localStorage.getItem('token');

  private user: string = localStorage.getItem('id') || '';

  constructor(private httpClient: HttpClient) { }

  getQuestions(page: number = 1, filters: any): Observable<QuestionResponse> {
    // Criando o cabeçalho com o token
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    // Criando os parâmetros de consulta
    const params = {
      page: page.toString(),
      ...filters // Adiciona os filtros como parâmetros de consulta
    };

    // Retornando o Observable com a tipagem correta
    return this.httpClient.get<QuestionResponse>(`${this.url}/questions`, { headers, params });
  }

  getUserAnsweredQuestions(page: number, filters: any): Observable<QuestionResponse> {
    // Criando o cabeçalho com o token
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    // Obter o ID do usuário do localStorage
    const userId = localStorage.getItem('id') || '';

    // Criando os parâmetros de consulta usando HttpParams
    let params = new HttpParams()
      .set('page', page.toString())
      .set('user_id', userId); // Adiciona o user_id como parâmetro

    // Adicione os parâmetros de filtro
    if (filters.questionType) params = params.set('question_type', filters.questionType);
    if (filters.difficulty) params = params.set('difficulty', filters.difficulty);
    if (filters.positions) params = params.set('positions', filters.positions);
    if (filters.bancas) params = params.set('bancas', filters.bancas);
    if (filters.subjects) params = params.set('subjects', filters.subjects);
    if (filters.topics) params = params.set('topics', filters.topics);
    if (filters.year) params = params.set('year', filters.year);
    if (filters.answerStatus) params = params.set('answer_status', filters.answerStatus);

    return this.httpClient.get<QuestionResponse>(`${this.url}/questions/answered`, { headers, params });
  }

  getQuestionsExam(examId: string, page: number = 1, filters: any = {}, perPage: number = 100): Observable<QuestionResponse> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    const params = {
      ...filters,
      page: page,
      per_page: perPage === 0 ? 'all' : perPage.toString(),
    };

    // URL com o examId dinâmico
    return this.httpClient.get<QuestionResponse>(`${this.url}/exams/${examId}/questions`, { headers, params });
  }

  toggleLike(qcode: string) {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    const payload = { qcode, user: this.user };

    return this.httpClient.post<simpleResponse>(this.url + '/likes', payload, { headers })
  }


  postFeedback(qcode: string, message: string) {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    const payload = { qcode, user: this.user, message };

    return this.httpClient.post<simpleResponse>(this.url + '/feedback', payload, { headers })
  }

  validateAnswer(qcode: string, answer: string) {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    const payload = { qcode, user: this.user, answer };

    return this.httpClient.post<simpleResponse>(this.url + '/answer', payload, { headers })
  }

  getUserLikes() {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    return this.httpClient.get<LikesResponse>(this.url + `/likes/${this.user}`, { headers });
  }

  hasAnswered(qcode: string) {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    return this.httpClient.get<hasAnsweredResponse>(this.url + `/answer/${qcode}/${this.user}`, { headers });
  }

  getComments(qcode: number) {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}` // Cabeçalho com o token
    });

    return this.httpClient.get<CommentResponse[]>(this.url + `/comments/${qcode}`, { headers });
  }

  postComment(qcode: number, comment: string) {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    const payload = {
      user_id: this.user,
      comment: comment,
      qcode: qcode
    };

    return this.httpClient.post<CommentResponse>(this.url + '/comments/create', payload, { headers });
  }

  deleteComment(commentId: number) {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    const payload = {
      user_id: this.user,
      id: commentId
    };

    return this.httpClient.post<simpleResponse>(this.url + '/comments/delete', payload, { headers });
  }

  getExams() {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}` // Include token for authentication
    });

    return this.httpClient.get<any[]>(this.url + '/exams', { headers });
  }

  getAttributes(): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    return this.httpClient.get<any>(`${this.url}/attributes`, { headers });
  }

  postHistory(
    title: string,
    examId: string,
    wrong: number,
    corrects: number,
    time: string,
    score: number
  ): Observable<simpleResponse> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    const payload = {
      user_id: this.user,
      title: title,
      exam_id: examId,
      wrongs: wrong,
      corrects: corrects,
      time: time,
      score: score
    };

    return this.httpClient.post<simpleResponse>(
      `${this.url}/exams/history`,
      payload,
      { headers }
    );
  }

  getHistory(id: string): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.token}` // Adicionando o token para autenticação
    });

    return this.httpClient.get<any>(`${this.url}/exams/history/${id}`, { headers });
  }
}
